import React from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import { useLocation, useNavigate } from 'react-router-dom';
import color from 'color';
import VideocamOutlined from '@mui/icons-material/Videocam';

import { useStyles } from './ExecutionsList.styles'
import { Table } from '@shared/components/Table';
import { IExecutionsStore } from 'NeoAnalytics/stores';

import { useExecutionsUIStore, useExecutionsGridUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { getExecutionStatusMeta } from 'NeoAnalytics/executionSteps';
import { getGradingStatusMeta } from 'NeoAnalytics/gradingStatus';

function formatCompactDuration(s: string | null): string {
    if (!s) {return '—';}
    const m = s.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
    if (!m) {return '—';}
    const hours = Number(m[1]);
    const minutes = Number(m[2]);
    const seconds = Number(m[3]);
    const pad2 = (n: number) => String(n).padStart(2, '0');

    return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

function getSortValue(row: ExecutionRow, columnId: string): string | number {
    switch (columnId) {
                    case 'exeData_entityName': return (row.exeData.entityName ?? '').toLowerCase();
                    case 'exeData_startTime': return new Date(row.exeData.startTime ?? 0).getTime();
                    case 'exeData_platform': return (row.exeData.platform ?? '').toLowerCase();
                    case 'exeData_executionMode': return (row.exeData.executionMode ?? '').toLowerCase();
                    case 'exeData_duration': {
                        const dur = row.exeData.duration;
                        if (!dur) {return -1;}
                        const dm = dur.match(/^(\d{2}):(\d{2}):(\d{2})/);
                        if (!dm) {return -1;}

                        return Number(dm[1]) * 3600 + Number(dm[2]) * 60 + Number(dm[3]);
                    }
                    case 'exeData_entityVersion': return (row.exeData.entityVersion ?? '').toLowerCase();
                    default: return '';
    }
}

type ChartExecution = {
    id: string;
};

const COLORS = ['#0088FE', '#ffd500ff', '#00842aff', '#ff0000ff', '#ff00eeff'];

type FetchDataType = {
    pageIndex: number;
    sortBy: {
        id: string;
        desc: boolean;
    }[];
};

type GraphData = {
    inGraph: boolean;
    color?: string;
}

type ExecutionGradingSummary = {
    isGraded: boolean;
    isReviewed: boolean;
    reviewCount: number;
};

type ExecutionRow = {
    exeData: IExecutionsStore;
    graphData: GraphData;
    gradingSummary: ExecutionGradingSummary;
};

type ProcedureCellProps = {
    row: ExecutionRow;
    theme: ThemeProps;
    analyticsEntityId: string | null;
    showVersionBadge: boolean;
};

type TextCellProps = {
    value?: string | null;
    className: string;
};

type ActorCellProps = {
    actorIds: string[];
    steps?: Array<{ actorId?: string | null }>;
    events?: Array<{ actorId?: string | null }>;
    className: string;
    showStatus?: boolean;
    exeData?: IExecutionsStore;
    theme?: ThemeProps;
    gradingSummary?: ExecutionGradingSummary;
};

const getResolvedActorIds = ({ actorIds, steps = [], events = [] }: Pick<ActorCellProps, 'actorIds' | 'steps' | 'events'>) => {
    const resolvedActorIds = [
        ...actorIds,
        ...steps.map(step => step.actorId ?? ''),
        ...events.map(event => event.actorId ?? ''),
    ].filter((actorId): actorId is string => Boolean(actorId));

    return Array.from(new Set(resolvedActorIds));
};

const DEFAULT_GRADING_SUMMARY: ExecutionGradingSummary = {
    isGraded: false,
    isReviewed: false,
    reviewCount: 0,
};

const columnHelper = createColumnHelper<ExecutionRow>();

const ProcedureCell: React.FC<ProcedureCellProps> = ({ row, theme, analyticsEntityId, showVersionBadge }) => {
    const statusMeta = getExecutionStatusMeta(row.exeData, theme);
    const gradingStatusMeta = getGradingStatusMeta(row.gradingSummary, theme.isDark);

    return (
        <div style={{display: 'flex', alignContent: 'flex-start', alignItems: 'stretch', width: '100%', minWidth: 0}}>
            <div style={{
                width: 0,
                minWidth: 0,
                borderLeft: `5px solid ${statusMeta.color}`,
                borderRadius: '999px',
                alignSelf: 'stretch',
                minHeight: 'calc(100% - 4px)',
                margin: '2px 8px 2px 0',
            }}/>
            <div style={{whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25, flex: 1, minWidth: 0}}>
                <div>{row.exeData.entityName}</div>
                <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {showVersionBadge && row.exeData.entityVersion && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                color: theme.colors.indigo_primary,
                                backgroundColor: color(theme.colors.indigo_primary).fade(0.86).string(),
                                textTransform: 'uppercase',
                                letterSpacing: 0.35,
                            }}
                        >
                            {`v${row.exeData.entityVersion}`}
                        </div>
                    )}
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            color: statusMeta.labelColor ?? statusMeta.color,
                            backgroundColor: statusMeta.background,
                            textTransform: 'uppercase',
                            letterSpacing: 0.35,
                        }}
                    >
                        {statusMeta.label}
                    </div>
                    {gradingStatusMeta && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                color: gradingStatusMeta.color,
                                backgroundColor: gradingStatusMeta.backgroundColor,
                                textTransform: 'uppercase',
                                letterSpacing: 0.35,
                            }}
                        >
                            {gradingStatusMeta.label}
                        </div>
                    )}
                    {row.exeData.hasMedia && (
                        <div
                            title="Video available"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 6px',
                                borderRadius: 999,
                                color: theme.colors.indigo_primary,
                                backgroundColor: color(theme.colors.indigo_primary).fade(0.86).string(),
                            }}
                        >
                            <VideocamOutlined style={{ fontSize: 13 }} />
                        </div>
                    )}
                </div>
            </div>
            {analyticsEntityId && row.graphData.inGraph &&
                <div style={{
                    width: '30px',
                    height: '3px',
                    backgroundColor: row.graphData.color,
                    margin: '10px',
                }}/>
            }
        </div>
    );
};

const TextCell: React.FC<TextCellProps> = ({ value, className }) => (
    <div className={className}>
        {value ?? ''}
    </div>
);

const ActorCell: React.FC<ActorCellProps> = ({ actorIds, steps, events, className, showStatus, exeData, theme, gradingSummary }) => {
    const executionsGridUIStore = useExecutionsGridUIStore();

    return (
        <Observer>
            {() => {
                const actorLabels = executionsGridUIStore.actorLabels;
                const resolvedActorIds = getResolvedActorIds({ actorIds, steps, events });
                const value = resolvedActorIds
                    .map(actorId => actorLabels[actorId])
                    .filter((actorLabel): actorLabel is string => Boolean(actorLabel))
                    .join(', ');
                const hasPendingActorLabel = resolvedActorIds.some(actorId => !Object.prototype.hasOwnProperty.call(actorLabels, actorId));
                const isLoading = executionsGridUIStore.isActorsLoading || executionsGridUIStore.isExecutionsLoading || hasPendingActorLabel;
                const actorName = value || (isLoading ? 'Loading Actor...' : 'Unknown actor');

                if (showStatus && exeData && theme) {
                    const statusMeta = getExecutionStatusMeta(exeData, theme);
                    const gradingStatusMeta = gradingSummary ? getGradingStatusMeta(gradingSummary, theme.isDark) : null;

                    return (
                        <div style={{ display: 'flex', alignContent: 'flex-start', alignItems: 'stretch', width: '100%', minWidth: 0 }}>
                            <div style={{
                                width: 0,
                                minWidth: 0,
                                borderLeft: `5px solid ${statusMeta.color}`,
                                borderRadius: '999px',
                                alignSelf: 'stretch',
                                minHeight: 'calc(100% - 4px)',
                                margin: '2px 8px 2px 0',
                            }} />
                            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                                <div>{actorName}</div>
                                <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: statusMeta.labelColor ?? statusMeta.color,
                                        backgroundColor: statusMeta.background,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.35,
                                    }}>
                                        {statusMeta.label}
                                    </div>
                                    {gradingStatusMeta && (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: gradingStatusMeta.color,
                                            backgroundColor: gradingStatusMeta.backgroundColor,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.35,
                                        }}>
                                            {gradingStatusMeta.label}
                                        </div>
                                    )}
                                    {exeData.hasMedia && (
                                        <div
                                            title="Video available"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '2px 6px',
                                                borderRadius: 999,
                                                color: theme.colors.indigo_primary,
                                                backgroundColor: color(theme.colors.indigo_primary).fade(0.86).string(),
                                            }}
                                        >
                                            <VideocamOutlined style={{ fontSize: 13 }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className={className} style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>
                        {actorName}
                    </div>
                );
            }}
        </Observer>
    );
};

function getColumns(
    theme: ThemeProps,
    styles: ReturnType<typeof useStyles>,
    analyticsEntityId: string | null,
    showActorColumn: boolean,
    isCompact: boolean,
): ColumnDef<ExecutionRow, any>[] {
    const columns: ColumnDef<ExecutionRow, any>[] = [
        ...(!showActorColumn ? [
            columnHelper.accessor('exeData.entityName', {
                header: 'Procedures',
                meta: { cardRole: 'title' },
                size: 42,
                cell: ({ row }) => (
                    <ProcedureCell
                        row={row.original}
                        theme={theme}
                        analyticsEntityId={analyticsEntityId}
                        showVersionBadge={false}
                    />
                ),
            }),
        ] : []),
        columnHelper.accessor('exeData.startTime', {
            header: 'Date',
            meta: { cardRole: 'subtitle' },
            size: showActorColumn ? 16 : 18,
            cell: ({ getValue }) => {
                const value = getValue();
                const dateTime = new Date(value!);
                const date = dateTime.toLocaleDateString();
                const time = dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                return (
                    <div style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>
                        {`${date} - ${time}`}
                    </div>
                );
            },
        }),
        columnHelper.accessor('exeData.executionMode', {
            header: 'Mode',
            meta: { cardRole: 'chip' },
            size: 4,
            maxSize: 150,
            cell: ({ getValue }) => <TextCell className={styles.dataContainer} value={getValue()} />,
        }),
        ...(!isCompact ? [
            columnHelper.accessor('exeData.platform', {
                header: 'Platform',
                meta: { cardRole: 'chip' },
                size: 12,
                maxSize: 180,
                cell: ({ getValue }) => <TextCell className={styles.dataContainer} value={getValue()} />,
            }),
            columnHelper.accessor('exeData.duration', {
                header: 'Duration',
                meta: { cardRole: 'badge', cardOrder: 2 },
                size: 6,
                maxSize: 90,
                sortDescFirst: true,
                cell: ({ getValue }) => {
                    const value = getValue() as string | null;

                    return <TextCell className={styles.dataContainer} value={formatCompactDuration(value)} />;
                },
            }),
        ] : []),
    ];

    if (showActorColumn) {
        columns.unshift(
            columnHelper.accessor('exeData.entityVersion', {
                header: 'Version',
                meta: { cardRole: 'badge', cardOrder: 1 },
                size: 10,
                cell: ({ getValue }) => <TextCell className={styles.dataContainer} value={getValue() ? `v${getValue()}` : '—'} />,
            }),
            columnHelper.display({
                id: 'actors',
                header: 'Actor',
                meta: { cardRole: 'title' },
                size: 30,
                cell: ({ row }) => (
                    <ActorCell
                        actorIds={row.original.exeData.actorIds.slice()}
                        steps={row.original.exeData.steps.slice()}
                        events={row.original.exeData.events.slice()}
                        className={styles.dataContainer}
                        showStatus={true}
                        exeData={row.original.exeData}
                        theme={theme}
                        gradingSummary={row.original.gradingSummary}
                    />
                ),
            }),
        );
    }

    return columns;
}

type ExecutionFilters = {
    platforms: string[];
    modes: string[];
    statuses: string[];
};

export type ExecutionsListProps = {
    hoveredExecutionId?: string | null;
    showActorColumn?: boolean;
    searchText?: string;
    localFilters?: ExecutionFilters;
};

export const ExecutionsList: React.FC<ExecutionsListProps> = ({ hoveredExecutionId = null, showActorColumn = false, searchText = '', localFilters }) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const location = useLocation();
    const navigate = useNavigate();

    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionsUIStore = useExecutionsUIStore();
    const isCompact = useBreakpointDown('tablet');
    const isPhoneCards = useBreakpointDown('mobile');

    const [localSorting, setLocalSorting] = React.useState<Array<{ id: string; desc: boolean }>>(
        [{ id: 'exeData_startTime', desc: true }]
    );

    const initialSortState = {
        sorting: [{ id: 'exeData_startTime', desc: true }],
    };

    const onAnalyse = React.useCallback((executionId: string) => {
        const basePath = location.pathname.replace(/\/executions\/[^/]+$/, '');
        navigate(`${basePath}/executions/${executionId}`);
        void executionsUIStore.loadExecution(executionId);
    }, [executionsUIStore, location.pathname, navigate]);

    const columns = React.useMemo(
        () => getColumns(theme, styles, executionsGridUIStore.analyticsEntityId, showActorColumn, isCompact && !isPhoneCards),
        [executionsGridUIStore.analyticsEntityId, showActorColumn, styles, theme, isCompact, isPhoneCards],
    );

    const onFetchData = ({ sortBy }: FetchDataType) => {
        setLocalSorting(sortBy && sortBy.length ? sortBy : []);
    };

    return (
        <div className={styles.tableContainer}>
            <Observer>
                {() => {
                    const rawData = executionsGridUIStore.executions.slice();
                    const graphExecutionColorMap = new Map(
                        executionsGridUIStore.chartExecutions.map((execution: ChartExecution, index: number) => [execution.id, COLORS[index % COLORS.length]])
                    );
                    let displayData: ExecutionRow[] = rawData.map((row: IExecutionsStore) => ({
                        exeData: row,
                        graphData: {
                            inGraph: graphExecutionColorMap.has(row.id),
                            color: graphExecutionColorMap.get(row.id),
                        },
                        gradingSummary: executionsGridUIStore.gradingSummaries[row.id] ?? DEFAULT_GRADING_SUMMARY,
                    }));

                    // Local search filter
                    if (searchText.trim()) {
                        const lower = searchText.trim().toLowerCase();
                        displayData = displayData.filter(row =>
                            row.exeData.entityName?.toLowerCase().includes(lower) ||
                            row.exeData.platform?.toLowerCase().includes(lower) ||
                            row.exeData.executionMode?.toLowerCase().includes(lower)
                        );
                    }

                    // Local filter by platform / mode / status
                    if (localFilters?.platforms.length) {
                        displayData = displayData.filter(row =>
                            row.exeData.platform != null && localFilters!.platforms.includes(row.exeData.platform)
                        );
                    }
                    if (localFilters?.modes.length) {
                        displayData = displayData.filter(row =>
                            row.exeData.executionMode != null && localFilters!.modes.includes(row.exeData.executionMode)
                        );
                    }
                    if (localFilters?.statuses.length) {
                        displayData = displayData.filter(row => {
                            const state = getExecutionStatusMeta(row.exeData, theme).state;

                            return localFilters!.statuses.includes(state);
                        });
                    }

                    // Local sort
                    if (localSorting.length > 0) {
                        const { id, desc } = localSorting[0];
                        displayData = [...displayData].sort((a, b) => {
                            const aVal = getSortValue(a, id);
                            const bVal = getSortValue(b, id);
                            if (aVal < bVal) {return desc ? 1 : -1;}
                            if (aVal > bVal) {return desc ? -1 : 1;}

                            return 0;
                        });
                    } else {
                        displayData = [...displayData].sort((a, b) =>
                            new Date(b.exeData.startTime!).getTime() - new Date(a.exeData.startTime!).getTime()
                        );
                    }

                    const genericColumns = columns as ColumnDef<{}, any>[];

                    return (
                        <Table
                            data={displayData}
                            columns={genericColumns}
                            fetchData={onFetchData}
                            isLoading={executionsGridUIStore.status.isLoading}
                            useSkeleton={true}
                            paginationStatus={{ ...executionsGridUIStore.pagination }}
                            initialState={initialSortState}
                            paginationDisabled
                            getRowOnClick={(rowData: { exeData: IExecutionsStore }) => onAnalyse(rowData.exeData.id)}
                            getRowClassName={(rowData: { exeData: IExecutionsStore }) => (
                                [
                                    styles.clickableRow,
                                    hoveredExecutionId && rowData.exeData.id === hoveredExecutionId ? styles.highlightedRow : undefined,
                                ].filter(Boolean).join(' ')
                            )}
                        />
                    );
                }}
            </Observer>
        </div>
    );
}